import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Stat, StatusBadge, btn } from "@/components/kit";
import ProgressRing from "@/components/ProgressRing";
import { Users, Plus, ArrowUpRight, AlertTriangle } from "lucide-react";

export default function OrgDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/saas/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="animate-pulse text-muted-foreground overline">Loading…</div>;

  const org = d.organization;
  const trial = org.subscription_status === "trial";

  return (
    <div>
      <PageHeader title={`Welcome to ${org.name}`} subtitle="Your onboarding pipeline at a glance.">
        <Link to="/app/clients" className={btn.primary} data-testid="dashboard-add-client"><Plus className="h-4 w-4" /> New client</Link>
      </PageHeader>

      {trial && (
        <div className="mb-8 border border-accent bg-accent/10 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="trial-banner">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-accent" />
            <p className="text-sm">You're on a <span className="font-semibold">free trial</span>. Subscribe to keep your workspace after it ends.</p>
          </div>
          <Link to="/app/billing" className="text-sm font-semibold text-accent hover:underline whitespace-nowrap">Choose a plan →</Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-8">
        <Stat label="Total clients" value={d.total_clients} />
        <Stat label="In progress" value={d.in_progress} />
        <Stat label="Awaiting review" value={d.awaiting_review} accent />
        <Stat label="Completed" value={d.completed} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 className="font-bold">Recent clients</h2>
            <Link to="/app/clients" className="text-sm text-accent hover:underline">View all</Link>
          </div>
          {d.recent_clients.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-3" />
              No clients yet. Create your first onboarding.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {d.recent_clients.map((c) => (
                <Link to={`/app/clients/${c.id}`} key={c.id} data-testid={`recent-client-${c.id}`}
                  className="flex items-center justify-between px-6 py-4 hover:bg-secondary/50 transition-colors group">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{c.company || c.email} · {c.flow_name}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <StatusBadge status={c.status} />
                    <ProgressRing value={c.progress} size={40} stroke={4} label={false} />
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="bg-card border border-border p-6">
          <h2 className="font-bold mb-4">Average progress</h2>
          <div className="grid place-items-center py-4"><ProgressRing value={d.avg_progress} size={140} stroke={10} /></div>
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Active flows</span><span className="font-semibold">{d.flows}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><span className="font-semibold capitalize">{org.plan}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Status</span><StatusBadge status={org.subscription_status} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}
